import { MutableRefObject, useCallback, useContext, useEffect, useState } from "react";
import {
	addOrUpdateReceiveToReceivesList,
	editLocationBay,
	enrichReceiveForManualReceive,
	getSkusOnInvoiceNotOnPurchaseOrder,
	mapEdiInvoiceToReceive,
	mapPartReceiveToBeFinalised,
	mapReceiveToBeFinalised,
	receiveStatus,
	receiveType as receiveTypes,
	validateInvoiceNumberAsync,
	validateReceiveAsync,
} from "../utils/receiveUtilExports";
import { getCannotProcessEdiInvoiceModal, getPurchaseOrderLockedModal, modals } from "../../../util/modalUtil";
import { loadingDialogClosed, modalClosed } from "../../../util/emptyObjectsUtil";
import { postReceiveFinalise, postReopenPurchaseOrderAsync, postSubmitReceiveAsync } from "../../../services/apiHelper";
import { AlertModalProps } from "../../../components/AlertModal/AlertModal";
import { AssignLocationProps, HistoryProps } from "../types";
import { Receive } from "../../../common/Receive";
import { UserContext } from "../../../providers/user";
import { useGetReceivePurchaseOrderDetails } from "../api/useGetReceivePurchaseOrderDetails";
import { useHistory } from "react-router-dom";
import { containsLockedReceiveForStatus } from "../../../util/receiveUtil";
import PageContext from "../../../components/Common/PageContext";
import { AxiosResponse } from "axios";
import { ReceiveType } from "../../../util/receiveTypeMapping";
import { ReceiveDetailsResponse } from "../../../common/api/Models/ReceiveDetailsResponse";
import { EdiInvoice } from "../../../common/EdiInvoice";
import { ManualInvoiceEditableFields } from "../../../components/Invoice/types";
import { usePostLineItemReceived } from "../../../common/api/usePostLineItemReceived";

export const useModal = (
	pageData: ReceiveDetailsResponse | undefined,
	activeTab: number,
	receive: Receive | undefined,
	receives: Receive[],
	productDetails: any[],
	originalReceives: Receive[],
	setModal: (modal: any) => void,
	setLoadingDialog: (dialog: any) => void,
	setReceive: (receive: Receive | undefined) => void,
	setReceives: (receives: Receive[]) => void,
	setActiveTab: (tab: number) => void,
	upsertReceive: (receive: Receive) => Promise<AxiosResponse<Receive>>,
	onCloseModal: () => void,
	returnToHome: () => void,
	onSetInProgressReceive: (receive: Receive) => void,
	clearCurrentReceive: () => void,
	setIsExpanded: (state: { header: boolean; invoiceDetails: boolean }) => void,
	welcomeModalShownThisSession: MutableRefObject<boolean>,
	isPartReceiveFeatureEnabled: boolean,
	receiveType: ReceiveType,
	initialised: boolean,
	setIsActionPrevented: (state: boolean) => void,
	invoiceFields: ManualInvoiceEditableFields,
	selectedCommercialAccount: string
) => {
	const [, fetchReceiveData] = useGetReceivePurchaseOrderDetails(
		pageData?.purchaseOrder?.sourceId ?? "",
		receive?.invoiceNumber ?? "",
		undefined,
		true
	);
	const [, postLineItemReceived] = usePostLineItemReceived();
	const { graphProfile } = useContext(UserContext);
	const [pageContext] = useContext(PageContext);
	const history = useHistory();
	const [isModalAsnDetailsOpen, setIsModalAsnDetailsOpen] = useState(false);
	const [isModalReceiveFinaliseOpen, setIsModalReceiveFinaliseOpen] = useState<boolean>(false);
	const [isModalForcePartReceiveOpen, setIsModalForcePartReceiveOpen] = useState<boolean>(false);
	const [isBackdropOpen, setIsBackdropOpen] = useState<boolean>(false);
	const [isSpeedDialOpen, setIsSpeedDialOpen] = useState<boolean>(false);
	const [receiveHistory, setReceiveHistory] = useState<HistoryProps>({
		isHistoryVisible: false,
		sku: "",
		invoiceNumber: "",
		statusHistoryList: [],
	});
	const [alertModal, setAlertModal] = useState<AlertModalProps>({
		isOpen: false,
		title: "",
		status: "success",
		onClose: () => {},
	});
	/* Changing the location bay for receive line item(s) */
	const [assignLocation, setAssignLocation] = useState<AssignLocationProps>({
		/* Set when changing the location bay for a specific sku */
		sku: "",
		/* The location bay currently assigned to the line item(s) */
		currentLocation: "",
		isModalOpen: false,
		/* The location bay being scanned by the user */
		scannedLocation: "",
		context: "manual",
	});

	const onOpenAsnDetailsModal = useCallback(() => {
		setIsModalAsnDetailsOpen(true);
	}, [setIsModalAsnDetailsOpen]);

	const onCloseAsnDetailsModal = useCallback(() => {
		setIsModalAsnDetailsOpen(false);
	}, [setIsModalAsnDetailsOpen]);

	const checkInvoiceNumberIsValidAsync = useCallback(
		async (invoiceNumber: string) => {
			if (!pageData?.purchaseOrder) return true;

			let activeReceive = activeTab === 1 ? receive : receives.find((r) => r.invoiceNumber === invoiceNumber);
			const validationResult = await validateInvoiceNumberAsync(
				invoiceNumber,
				pageData.purchaseOrder.sourceId,
				pageData.purchaseOrder.vendorId,
				activeReceive?.receiveType ?? receiveType,
				activeReceive?.id,
				receives
			);

			if (!validationResult.isValid || validationResult.isWarning) {
				setModal({
					...modals.invoiceAlreadyUsed,
					paragraphs: [validationResult.reason],
					onPrimaryButtonClick: onCloseModal,
					isWarning: validationResult.isWarning ?? false,
				});
			}
		},
		[activeTab, receive, receives, pageData?.purchaseOrder, setModal, onCloseModal, receiveType]
	);

	const onOpenSpeedDial = useCallback(() => {
		setIsSpeedDialOpen(true);
		setIsBackdropOpen(true);
	}, []);

	const onCloseSpeedDial = useCallback(() => {
		setIsSpeedDialOpen(false);
		setIsBackdropOpen(false);
	}, []);

	const validateReceiveForSubmission = useCallback(
		async (receiveToValidate: Receive, toBeFinalised = false) => {
			if (!pageData?.purchaseOrder) return { isValid: false, reasons: ["No purchase order"] };

			const enrichedLineItems = enrichReceiveForManualReceive(
				receiveToValidate,
				pageData.purchaseOrder,
				productDetails,
				originalReceives,
				pageData?.customerOrders?.inventory ?? []
			);
			try {
				setLoadingDialog({ isOpen: true, message: "Validating ..." });
				return await validateReceiveAsync(
					enrichedLineItems.receiveLineItems,
					receiveToValidate.receivedDate,
					receiveToValidate,
					receives,
					pageData.purchaseOrder.vendorId,
					toBeFinalised
				);
			} catch {
				return {
					isValid: false,
					reasons: [`Unable to validate receive with invoice number ${receiveToValidate.invoiceNumber}`],
				};
			} finally {
				setLoadingDialog(loadingDialogClosed);
			}
		},
		[pageData?.purchaseOrder, productDetails, receives, pageData?.customerOrders, originalReceives, setLoadingDialog]
	);

	const openFinaliseModal = useCallback(() => {
		setIsModalReceiveFinaliseOpen(true);
	}, [setIsModalReceiveFinaliseOpen]);

	const onSaveReceiveClick = useCallback(async () => {
		if (!receive) return;
		setLoadingDialog({ isOpen: true, message: "Saving receive" });

		const validationResults = await validateReceiveForSubmission(receive);
		if (validationResults.isValid) {
			try {
				const receiveToUpdate = {
					...receive,
					status: receiveStatus.inProgress,
					invoiceNumber: invoiceFields.invoiceNumber ?? "",
					invoiceDate: invoiceFields.invoiceDate ?? "",
					receivedDate: invoiceFields.receivedDate ?? "",
					notes: invoiceFields.notes ?? "",
					inventoryStatus: selectedCommercialAccount,
				};
				const updatedReceive = await upsertReceive(receiveToUpdate);

				const newReceives = addOrUpdateReceiveToReceivesList(receives, updatedReceive.data);

				setReceives(newReceives);
				setReceive(updatedReceive.data);

				setModal({
					...modals.saveSuccess,
					onPrimaryButtonClick: onCloseModal,
				});
			} catch {
				setModal({ ...modals.issueSaving, onPrimaryButtonClick: onCloseModal });
			}
		} else {
			setModal({
				isOpen: true,
				title: "Issue(s) saving",
				paragraphs: validationResults.reasons,
				primaryButtonName: "Ok",
				onPrimaryButtonClick: onCloseModal,
			});
		}

		setLoadingDialog(loadingDialogClosed);
		onCloseSpeedDial();
	}, [
		receive,
		setReceive,
		receives,
		setReceives,
		upsertReceive,
		onCloseModal,
		onCloseSpeedDial,
		setLoadingDialog,
		setModal,
		validateReceiveForSubmission,
		invoiceFields,
		selectedCommercialAccount,
	]);

	const onSaveDraftClick = useCallback(async () => {
		setModal({
			...modals.saveDraft,
			onPrimaryButtonClick: async () => {
				await onSaveReceiveClick();
				clearCurrentReceive();
			},
		});
	}, [onSaveReceiveClick, clearCurrentReceive, setModal]);

	const onSaveReceivesInReview = useCallback(async () => {
		setLoadingDialog({ isOpen: true, message: "Saving receives" });

		let hasError = false;
		const updatedReceives = await Promise.all(
			receives.map(async (receiveInReview) => {
				if (hasError || receiveInReview.status !== receiveStatus.reviewing) {
					return receiveInReview as Receive;
				}

				const receiveToUpdate = {
					...receiveInReview,
					modifiedBy: graphProfile.displayName,
					modifiedById: graphProfile.staffId,
				};

				const response = await upsertReceive(receiveToUpdate);
				if (response.status !== 200) {
					hasError = true;
					setModal({
						...modals.genericError,
						title: `Issue saving receive with invoice number ${receiveInReview.invoiceNumber}`,
						primaryButtonName: "Ok",
						onPrimaryButtonClick: onCloseModal,
					});
					return receiveInReview;
				}
				return response.data;
			})
		);

		setReceives(updatedReceives);

		setModal({
			...modals.saveSuccess,
			onPrimaryButtonClick: onCloseModal,
		});
		setLoadingDialog(loadingDialogClosed);
	}, [receives, graphProfile, upsertReceive, setReceives, setModal, onCloseModal, setLoadingDialog]);

	const onSaveReceivesInReviewClick = useCallback(async () => {
		setModal({
			...modals.saveChanges,
			onPrimaryButtonClick: async () => {
				await onSaveReceivesInReview();
				clearCurrentReceive();
			},
		});
	}, [clearCurrentReceive, setModal, onSaveReceivesInReview]);

	const onSubmitForReviewClick = useCallback(async () => {
		if (!receive) return;

		const validationResults = await validateReceiveForSubmission(receive);
		if (validationResults.isValid) {
			openFinaliseModal();
		} else {
			setModal({
				isOpen: true,
				title: "Cannot submit for review",
				paragraphs: validationResults.reasons,
				primaryButtonName: "Ok",
				onPrimaryButtonClick: onCloseModal,
			});
		}
	}, [receive, validateReceiveForSubmission, openFinaliseModal, onCloseModal, setModal]);

	const onSubmitForReview = useCallback(
		async (
			customerOrders: any[],
			hasLabels: boolean,
			skipAutoOrderAllocation: boolean,
			skipProductLabelPrinting: boolean
		) => {
			setLoadingDialog({ isOpen: true, message: "Submitting for review" });
			setModal(modalClosed);

			const { selectedPrinterId, selectedLabelPrinterId, userLocation } = pageContext;
			try {
				const updateReceive = {
					...receive,
					receiveType: receiveTypes.manual,
					status: receiveStatus.pendingReview,
					modifiedBy: graphProfile.displayName,
					modifiedById: graphProfile.staffId,
					invoiceNumber: invoiceFields.invoiceNumber ?? receive?.invoiceNumber ?? "",
					invoiceDate: invoiceFields.invoiceDate ?? receive?.invoiceDate ?? "",
					receivedDate: invoiceFields.receivedDate ?? receive?.receivedDate ?? "",
					notes: invoiceFields.notes ?? receive?.notes ?? "",
					inventoryStatus: selectedCommercialAccount,
				};
				await postSubmitReceiveAsync(
					updateReceive,
					customerOrders,
					selectedPrinterId,
					selectedLabelPrinterId,
					userLocation,
					false,
					hasLabels,
					skipAutoOrderAllocation,
					skipProductLabelPrinting
				);
				setModal({
					...modals.submitSuccess,
					onPrimaryButtonClick: onCloseModal,
					onExitClick: onCloseModal,
				});
				setIsModalReceiveFinaliseOpen(false);
				setReceives(addOrUpdateReceiveToReceivesList(receives, updateReceive));
				setActiveTab(2);
				setReceive(undefined);
				setIsExpanded({ header: true, invoiceDetails: true });
			} catch {
				setModal({ ...modals.issueSaving, onPrimaryButtonClick: onCloseModal });
			} finally {
				setLoadingDialog(loadingDialogClosed);
				onCloseSpeedDial();
			}
		},
		[
			receive,
			receives,
			graphProfile,
			setLoadingDialog,
			setModal,
			setReceives,
			setActiveTab,
			setReceive,
			onCloseSpeedDial,
			onCloseModal,
			pageContext,
			setIsExpanded,
			invoiceFields,
			selectedCommercialAccount,
		]
	);

	const onFinaliseReceivesInReview = useCallback(async () => {
		setLoadingDialog({ isOpen: true, message: "Finalising ..." });

		try {
			let hasError = false;
			const updatedReceives = await Promise.all(
				receives.map(async (receiveInReview) => {
					if (receiveInReview.status !== receiveStatus.reviewing || hasError) {
						return receiveInReview;
					}

					const receiveToUpdate = {
						...receiveInReview,
						status: receiveStatus.pendingFinalise,
						receiver: graphProfile.displayName,
						receiverId: graphProfile.staffId,
						failureReason: null,
						receiptAttemptCount: 0,
					};

					try {
						// todo should try and adopt postSubmitReceiveAsync, instead of directly posting to ReceiveApi/Receive/Finalise
						const finaliseResponse = await postReceiveFinalise(receiveToUpdate);
						if (finaliseResponse.status !== 200) {
							hasError = true;
						}
					} catch {
						hasError = true;
					}

					if (hasError) {
						setModal({
							...modals.genericError,
							title: `Issue(s) finalising receive with invoice number ${receiveInReview.invoiceNumber}`,
							primaryButtonName: "Ok",
							onPrimaryButtonClick: onCloseModal,
						});
						return receiveInReview;
					}
					return receiveToUpdate;
				})
			);
			setReceives(updatedReceives);

			if (!hasError) {
				setModal({
					...modals.saveSuccess,
					onPrimaryButtonClick: onCloseModal,
				});
			}
		} finally {
			setLoadingDialog(loadingDialogClosed);
		}
	}, [receives, graphProfile, setReceives, setModal, onCloseModal, setLoadingDialog]);

	const onFinaliseReceivesInReviewClick = useCallback(async () => {
		setLoadingDialog({ isOpen: true, message: "Validating ..." });
		const receivesInReview = receives.filter((r) => r.status === receiveStatus.reviewing);
		if (receivesInReview.length === 0) {
			setModal({
				...modals.cannotFinaliseReceiveBeforeReview,
				onPrimaryButtonClick: onCloseModal,
			});
			setLoadingDialog(loadingDialogClosed);
			return;
		}

		for (const receiveInReview of receivesInReview) {
			const validationResults = await validateReceiveForSubmission(receiveInReview, true);
			if (!validationResults.isValid) {
				setModal({
					isOpen: true,
					title: `Issue(s) finalising receive with invoice number ${receiveInReview.invoiceNumber}`,
					paragraphs: validationResults.reasons,
					primaryButtonName: "Ok",
					onPrimaryButtonClick: onCloseModal,
				});
				setLoadingDialog(loadingDialogClosed);
				return;
			}
		}

		setModal({
			...modals.finaliseRecieves,
			onPrimaryButtonClick: async () => {
				await onFinaliseReceivesInReview();
			},
		});
		setLoadingDialog(loadingDialogClosed);
	}, [
		receives,
		setModal,
		onCloseModal,
		validateReceiveForSubmission,
		onFinaliseReceivesInReview,
		setLoadingDialog,
	]);

	const confirmFinalise = useCallback(async () => {
		if (!receive) return;

		const validationResults = await validateReceiveForSubmission(receive, true);
		if (validationResults.isValid) {
			openFinaliseModal();
		} else {
			setModal({
				isOpen: true,
				title: "Cannot finalise receive",
				paragraphs: validationResults.reasons,
				primaryButtonName: "Ok",
				onPrimaryButtonClick: onCloseModal,
			});
		}
	}, [receive, onCloseModal, openFinaliseModal, setModal, validateReceiveForSubmission]);

	const confirmPartReceive = useCallback(async () => {
		if (!receive) return;

		const validationResults = await validateReceiveForSubmission(receive, true);
		if (validationResults.isValid) {
			setIsModalForcePartReceiveOpen(true);
		} else {
			setModal({
				isOpen: true,
				title: "Cannot finalise part receive",
				paragraphs: validationResults.reasons,
				primaryButtonName: "Ok",
				onPrimaryButtonClick: onCloseModal,
			});
		}
	}, [receive, onCloseModal, setModal, validateReceiveForSubmission]);

	const finalisePartReceive = useCallback(async () => {
		setLoadingDialog({ isOpen: true, message: "Submitting part receive" });
		setModal(modalClosed);

		try {
			const updatedReceive = mapPartReceiveToBeFinalised(receive, graphProfile);
			await postReceiveFinalise(updatedReceive);
			const newReceives = addOrUpdateReceiveToReceivesList(receives, updatedReceive);

			setModal({
				...modals.forcePartReceiveSuccessfulFinalise,
				onPrimaryButtonClick: returnToHome,
				onExitClick: onCloseModal,
			});
			setIsModalForcePartReceiveOpen(false);
			setReceives(newReceives);
			setActiveTab(2);
			setReceive(undefined);
		} catch {
			setModal({ ...modals.issueFinalisingReceive, onPrimaryButtonClick: onCloseModal });
		}

		setLoadingDialog(loadingDialogClosed);
		onCloseSpeedDial();
	}, [
		receive,
		receives,
		graphProfile,
		setLoadingDialog,
		setModal,
		setReceives,
		setActiveTab,
		setReceive,
		onCloseSpeedDial,
		onCloseModal,
		returnToHome,
	]);

	const finaliseReceive = useCallback(
		async (
			customerOrders: any[],
			hasLabels: boolean,
			skipAutoOrderAllocation: boolean,
			skipProductLabelPrinting: boolean
		) => {
			setLoadingDialog({ isOpen: true, message: "Submitting receive" });
			setModal(modalClosed);

			const { selectedPrinterId, selectedLabelPrinterId, userLocation } = pageContext;
			try {
				// Update receive with the latest invoice fields before finalising
				const receiveWithUpdatedFields = {
					...receive,
					invoiceNumber: invoiceFields.invoiceNumber ?? receive?.invoiceNumber ?? "",
					invoiceDate: invoiceFields.invoiceDate ?? receive?.invoiceDate ?? "",
					receivedDate: invoiceFields.receivedDate ?? receive?.receivedDate ?? "",
					notes: invoiceFields.notes ?? receive?.notes ?? "",
					inventoryStatus: selectedCommercialAccount,
				};
				const updatedReceive = mapReceiveToBeFinalised(receiveWithUpdatedFields, graphProfile);
				await postSubmitReceiveAsync(
					updatedReceive,
					customerOrders,
					selectedPrinterId,
					selectedLabelPrinterId,
					userLocation,
					true,
					hasLabels,
					skipAutoOrderAllocation,
					skipProductLabelPrinting
				);

				setModal({
					...modals.ediLikeAManualSuccessfulFinalise,
					onPrimaryButtonClick: returnToHome,
					onExitClick: onCloseModal,
				});
				setIsModalReceiveFinaliseOpen(false);

				const newReceives = addOrUpdateReceiveToReceivesList(receives, updatedReceive);
				setReceives(newReceives);
				setReceives(newReceives);
				setActiveTab(2);
				setReceive(undefined);
			} catch {
				setModal({ ...modals.issueFinalisingReceive, onPrimaryButtonClick: onCloseModal });
			} finally {
				setLoadingDialog(loadingDialogClosed);
				onCloseSpeedDial();
			}
		},
		[
			receive,
			receives,
			graphProfile,
			setLoadingDialog,
			setModal,
			setReceives,
			setActiveTab,
			setReceive,
			onCloseSpeedDial,
			onCloseModal,
			returnToHome,
			pageContext,
			invoiceFields,
			selectedCommercialAccount,
		]
	);

	const continueToEdi = useCallback(
		async (ediInvoice: EdiInvoice) => {
			try {
				const updatedReceive = mapEdiInvoiceToReceive(ediInvoice, receive, pageData?.purchaseOrder);
				await upsertReceive(updatedReceive);
				history.push(
					`/receiving?invoiceNumber=${ediInvoice.invoiceNumber}&purchaseOrderNumber=${
						pageData?.purchaseOrder?.sourceId ?? ""
					}`
				);
			} catch (e) {
				const skus = getSkusOnInvoiceNotOnPurchaseOrder(ediInvoice, pageData?.purchaseOrder);
				setModal({
					...getCannotProcessEdiInvoiceModal(skus),
					onPrimaryButtonClick: onCloseModal,
				});
			}
		},
		[receive, pageData?.purchaseOrder, upsertReceive, history, setModal, onCloseModal]
	);

	const checkForEdi = useCallback(async () => {
		try {
			setLoadingDialog({ isOpen: true, message: "Checking for EDI..." });
			const receiveDetailsResponse = await fetchReceiveData();
			const ediInvoice = receiveDetailsResponse?.data?.invoice;
			if (ediInvoice) {
				setModal({
					...modals.ediCheck,
					onPrimaryButtonClick: () => continueToEdi(ediInvoice),
				});
				setLoadingDialog(loadingDialogClosed);
			} else {
				setModal({
					isOpen: true,
					title: "Oh Snap! We couldn't find any open supplier EDIs matching this receive.",
					paragraphs: [
						"Please continue scanning the items as per manual receive process and submit the receive. We will match the receive to the EDI automatically when it is made available by the supplier.",
					],
					primaryButtonName: "Continue EDI like manual receive",
					onPrimaryButtonClick: onCloseModal,
				});
				setLoadingDialog(loadingDialogClosed);
			}
		} catch (error) {
			setModal({
				...modals.genericError,
				primaryButtonName: "Ok",
				onPrimaryButtonClick: onCloseModal,
			});
			setLoadingDialog(loadingDialogClosed);
		}
	}, [setLoadingDialog, onCloseModal, fetchReceiveData, continueToEdi, setModal]);

	const checkReceiveHistoryClick = useCallback(async () => {
		setReceiveHistory({
			isHistoryVisible: true,
			sku: "",
			invoiceNumber: receive ? receive.invoiceNumber : "",
			statusHistoryList: receive ? receive.statusHistory ?? [] : [],
		});
	}, [setReceiveHistory, receive]);

	const onReceiveHistoryClick = useCallback(
		async (receiveItem: any) => {
			setReceiveHistory({
				isHistoryVisible: true,
				sku: "",
				invoiceNumber: receiveItem ? receiveItem.invoiceNumber : "",
				statusHistoryList: receiveItem ? receiveItem.statusHistory ?? [] : [],
			});
		},
		[setReceiveHistory]
	);

	const onReopenPoClick = useCallback(async () => {
		try {
			setLoadingDialog({ isOpen: true, message: "Reopening Purchase order..." });
			await postReopenPurchaseOrderAsync(pageData?.purchaseOrder?.sourceId ?? "");
			setLoadingDialog(loadingDialogClosed);
			setModal({
				...modals.reopenPurchaseOrderSuccess,
				onPrimaryButtonClick: returnToHome,
			});
		} catch (e) {
			setLoadingDialog(loadingDialogClosed);
			setModal({
				...modals.reopenPurchaseOrderError,
				onPrimaryButtonClick: onCloseModal,
			});
		}
	}, [pageData?.purchaseOrder, setLoadingDialog, returnToHome, onCloseModal, setModal]);

	const onClickClearReceive = useCallback(
		async (invoiceNumber: string) => {
			setModal({
				...modals.clearReceive,
				onPrimaryButtonClick: () => {
					let updatedReceive =
						activeTab === 1
							? // the null-check is for when the user hasn't started scanning.
							  // the user wouldn't be clearing a Receive that hasn't been scanned, so this is a type safety check
							  receive
								? { ...receive }
								: null
							: receives.find((r) => r.invoiceNumber === invoiceNumber);

					if (!updatedReceive) return;

					updatedReceive.status = receiveStatus.cancelled;
					upsertReceive(updatedReceive).then(() => {
						if (activeTab === 1) {
							setReceive(undefined);
						}
						setModal(modalClosed);
						setIsExpanded({
							header: true,
							invoiceDetails: true,
						});

						const newReceives = addOrUpdateReceiveToReceivesList(receives, updatedReceive);
						setReceives(newReceives);
					});
				},
				onSecondaryButtonClick: onCloseModal,
			});
		},
		[activeTab, receive, receives, upsertReceive, setReceive, setReceives, setModal, setIsExpanded, onCloseModal]
	);

	const onStartReview = useCallback(async () => {
		let hasError = false;
		try {
			setLoadingDialog({ isOpen: true, message: "Starting review" });
			setModal(modalClosed);

			const updatedReceives = await Promise.all(
				receives.map(async (receiveInReview) => {
					if (receiveInReview.status !== receiveStatus.pendingReview || hasError) {
						return receiveInReview as Receive;
					}

					const receiveToUpdate = {
						...receiveInReview,
						status: receiveStatus.reviewing,
						modifiedBy: graphProfile.displayName,
						modifiedById: graphProfile.staffId,
						receiveType: receiveTypes.manual,
					};

					const response = await upsertReceive(receiveToUpdate);
					if (response.status !== 200) {
						hasError = true;
						setModal({
							...modals.genericError,
							title: `Issue(s) starting review of receive with invoice number ${receiveInReview.invoiceNumber}`,
							primaryButtonName: "Ok",
							onPrimaryButtonClick: onCloseModal,
						});
						return receiveInReview;
					}
					return response.data as Receive;
				})
			);
			setReceives(updatedReceives);
		} finally {
			setLoadingDialog(loadingDialogClosed);
		}
	}, [receives, graphProfile, upsertReceive, setReceives, setModal, onCloseModal, setLoadingDialog]);

	const onUnlockReceiveWithStatus = useCallback(
		async (status: string) => {
			try {
				setLoadingDialog({ isOpen: true, message: "Unlocking ..." });
				setModal(modalClosed);

				let hasError = false;
				const updatedReceives = await Promise.all(
					receives.map(async (receiveInReview) => {
						if (receiveInReview.status !== status || hasError) {
							return receiveInReview as Receive;
						}

						const receiveToUpdate = {
							...receiveInReview,
							status: status === receiveStatus.inProgress ? receiveStatus.inProgress : receiveStatus.pendingReview,
							modifiedBy: graphProfile.displayName,
							modifiedById: graphProfile.staffId,
						};
						const response = await upsertReceive(receiveToUpdate);
						if (response.status !== 200) {
							hasError = true;
							setModal({
								...modals.issueUnlockingPurchaseOrder,
								onPrimaryButtonClick: onCloseModal,
							});
							return receiveInReview;
						}
						return response.data as Receive;
					})
				);
				setReceives(updatedReceives);
			} finally {
				setLoadingDialog(loadingDialogClosed);
			}
		},
		[receives, graphProfile, upsertReceive, setReceives, setModal, onCloseModal, setLoadingDialog]
	);

	const onReceiveEditClick = useCallback(
		async (invoiceNumber: string) => {
			const receiveToEdit = receives.find((r) => r.invoiceNumber === invoiceNumber);
			if (!receiveToEdit) return;

			if (receiveToEdit.receiveType === receiveTypes.manual) {
				const lockedReceive = containsLockedReceiveForStatus(receives, graphProfile.staffId, receiveStatus.inProgress);
				if (lockedReceive) {
					const purchaseOrderLockedBasedModal = getPurchaseOrderLockedModal(lockedReceive);
					setModal({
						...purchaseOrderLockedBasedModal,
						onPrimaryButtonClick: onCloseModal,
						onSecondaryButtonClick: () => onUnlockReceiveWithStatus(receiveStatus.inProgress),
					});
					return;
				}
			}

			setModal({
				...modals.editReceive,
				onPrimaryButtonClick: () => {
					if (receiveToEdit) {
						onSetInProgressReceive(receiveToEdit);
					}
					setActiveTab(1);
				},
				onSecondaryButtonClick: onCloseModal,
			});
		},
		[
			graphProfile.staffId,
			onCloseModal,
			onUnlockReceiveWithStatus,
			receives,
			setModal,
			onSetInProgressReceive,
			setActiveTab,
		]
	);

	const onStartReviewClick = useCallback(async () => {
		const receivePendingReview = receives.find((r) => r.status === receiveStatus.pendingReview);
		if (!receivePendingReview) {
			setModal({
				...modals.noReceivesForReview,
				onPrimaryButtonClick: onCloseModal,
			});
			return;
		}

		const lockedReceive = containsLockedReceiveForStatus(receives, graphProfile.staffId, receiveStatus.reviewing);
		if (!lockedReceive) {
			setModal({
				...modals.beginReview,
				onPrimaryButtonClick: onStartReview,
			});
			onCloseSpeedDial();
		} else {
			const purchaseOrderLockedBasedModal = getPurchaseOrderLockedModal(lockedReceive);
			setModal({
				...purchaseOrderLockedBasedModal,
				onPrimaryButtonClick: onCloseModal,
				onSecondaryButtonClick: () => onUnlockReceiveWithStatus(receiveStatus.reviewing), // TODO RA-337 - this does nothing - user is trying to lock PO, but there are in-progress receives, this will simply change Reviewing -> Pending Review - and won't let it be locked. Should be onStartReview IF user is allowed to lock for review while in progress receives
			});
		}
	}, [
		receives,
		graphProfile.staffId,
		setModal,
		onCloseModal,
		onStartReview,
		onCloseSpeedDial,
		onUnlockReceiveWithStatus,
	]);

	const onCancelReviewClick = useCallback(async () => {
		let hasError = false;
		try {
			setLoadingDialog({ isOpen: true, message: "Unlocking ..." });
			setModal(modalClosed);

			const updatedReceives = await Promise.all(
				receives.map(async (receiveInReview) => {
					if (receiveInReview.status !== receiveStatus.reviewing || hasError) {
						return receiveInReview as Receive;
					}

					const receiveToUpdate = {
						...receiveInReview,
						status: receiveStatus.pendingReview,
						modifiedBy: graphProfile.displayName,
						modifiedById: graphProfile.staffId,
					};
					const response = await upsertReceive(receiveToUpdate);
					if (response.status !== 200) {
						hasError = true;
						setModal({
							...modals.genericError,
							title: `Issue(s) cancelling review of receive with invoice number ${receiveInReview.invoiceNumber}`,
							primaryButtonName: "Ok",
							onPrimaryButtonClick: onCloseModal,
						});
						return receiveInReview;
					}
					return response.data as Receive;
				})
			);
			setReceives(updatedReceives);
		} finally {
			setLoadingDialog(loadingDialogClosed);
		}
	}, [receives, graphProfile, upsertReceive, setReceives, setModal, onCloseModal, setLoadingDialog]);

	const onLocationNameChange = useCallback(
		async (currentLocation: string, sku?: string) => {
			setAssignLocation({
				...assignLocation,
				sku: sku ?? "",
				currentLocation,
				context: "manual",
				scannedLocation: "",
				isModalOpen: true,
			});
		},
		[setAssignLocation, assignLocation]
	);

	const onLocationSubmit = useCallback(
		async (previousLocation: string, updatedLocation: string, sku?: string) => {
			setAssignLocation({
				...assignLocation,
				sku: "",
				currentLocation: "",
				context: "manual",
				scannedLocation: "",
				isModalOpen: false,
			});

			if (!receive) return;

			const newLineItems = editLocationBay(receive.receiveLineItems, previousLocation, updatedLocation, sku);

			if (sku) {
				// Save the line item
				await postLineItemReceived({
					...receive.receiveLineItems.find((rli) => rli.sku === sku)!,
					receiveId: receive.id,
					modifiedBy: graphProfile.displayName,
					modifiedById: graphProfile.staffId,
					modifiedDate: new Date().toISOString(),
				});
			} else {
				// Save the entire receive
				await upsertReceive({
					...receive,
					receiveLineItems: newLineItems,
					modifiedBy: graphProfile.displayName,
					modifiedById: graphProfile.staffId,
				});
			}

			setReceive({
				...receive,
				receiveLineItems: newLineItems,
			});

			setAlertModal({ isOpen: true, title: "Location updated", status: "success" });
		},
		[
			setAssignLocation,
			assignLocation,
			setAlertModal,
			receive,
			setReceive,
			graphProfile,
			postLineItemReceived,
			upsertReceive,
		]
	);

	const onCloseModalReceiveFinalise = useCallback(() => {
		setIsModalReceiveFinaliseOpen(false);
	}, [setIsModalReceiveFinaliseOpen]);

	const onCloseAlertModal = useCallback(() => {
		setAlertModal({
			...alertModal,
			isOpen: false,
		});
	}, [setAlertModal, alertModal]);

	const onCloseModalForcePartReceive = useCallback(() => {
		setIsModalForcePartReceiveOpen(false);
	}, []);

	const closingHistoryModal = useCallback(() => {
		setReceiveHistory({
			isHistoryVisible: false,
			sku: "",
			invoiceNumber: "",
			statusHistoryList: [],
		});
	}, [setReceiveHistory]);

	useEffect(() => {
		if (!initialised || !pageData || welcomeModalShownThisSession.current) return;

		if (receiveType === receiveTypes.ediLikeAManual) {
			if (pageData.matchedReceive && pageData.invoice) {
				// The invoice from the supplier is now available - attempt to redirect to the EDI receiving page
				const skus = getSkusOnInvoiceNotOnPurchaseOrder(pageData.invoice, pageData.purchaseOrder);
				if (skus.length) {
					setIsActionPrevented(true);
					setModal({
						...getCannotProcessEdiInvoiceModal(skus),
						onPrimaryButtonClick: () => setModal(modalClosed),
						onExitClick: () => setModal(modalClosed),
					});
					return;
				}
				setModal({
					...modals.ediCheck,
					onPrimaryButtonClick: () => continueToEdi(pageData.invoice),
					onExitClick: () => setModal(modalClosed),
				});
				return;
			}

			if (activeTab === 1) {
				if (!isPartReceiveFeatureEnabled) {
					setModal({
						...modals.ediLikeAManualExplanation,
						onPrimaryButtonClick: () => setModal(modalClosed),
						onExitClick: () => setModal(modalClosed),
					});
				} else {
					setModal({
						...modals.forcePartReceiveExplanation,
						onPrimaryButtonClick: () => setModal(modalClosed),
						onExitClick: () => setModal(modalClosed),
					});
				}
			}
		} else if (receiveType === receiveTypes.manual && activeTab === 1) {
			const inProgressReceive = pageData.receives.find((r) => r.status === receiveStatus.inProgress);
			if (inProgressReceive) {
				const lockedReceive = containsLockedReceiveForStatus(
					pageData.receives,
					graphProfile.staffId,
					receiveStatus.inProgress
				);
				if (lockedReceive) {
					const purchaseOrderLockedBasedModal = getPurchaseOrderLockedModal(lockedReceive);
					setModal({
						...purchaseOrderLockedBasedModal,
						onPrimaryButtonClick: () => setModal(modalClosed),
						onExitClick: () => setModal(modalClosed),
						onSecondaryButtonClick: () => onUnlockReceiveWithStatus(receiveStatus.inProgress),
					});
					setActiveTab(2);
					return;
				} else {
					setReceive(inProgressReceive);
				}
			}

			setModal({
				...modals.manualExplanation,
				onPrimaryButtonClick: () => setModal(modalClosed),
				onExitClick: () => setModal(modalClosed),
			});
		}

		welcomeModalShownThisSession.current = true;
	}, [
		welcomeModalShownThisSession,
		activeTab,
		receiveType,
		pageData,
		graphProfile,
		isPartReceiveFeatureEnabled,
		setModal,
		setActiveTab,
		onUnlockReceiveWithStatus,
		initialised,
		setReceive,
		continueToEdi,
		setIsActionPrevented,
	]);

	return {
		isModalAsnDetailsOpen,
		isModalReceiveFinaliseOpen,
		isModalForcePartReceiveOpen,
		isBackdropOpen,
		isSpeedDialOpen,
		receiveHistory,
		alertModal,
		assignLocation,
		setAssignLocation,
		checkInvoiceNumberIsValidAsync,
		onOpenAsnDetailsModal,
		onCloseAsnDetailsModal,
		onOpenSpeedDial,
		onCloseSpeedDial,
		onSaveReceiveClick,
		onSubmitForReviewClick,
		onSubmitForReview,
		onFinaliseReceivesInReviewClick,
		confirmFinalise,
		confirmPartReceive,
		finalisePartReceive,
		finaliseReceive,
		checkForEdi,
		checkReceiveHistoryClick,
		onReceiveHistoryClick,
		onReopenPoClick,
		onReceiveEditClick,
		onClickClearReceive,
		onSaveDraftClick,
		onSaveReceivesInReviewClick,
		onStartReviewClick,
		onCancelReviewClick,
		onLocationNameChange,
		onLocationSubmit,
		onCloseModalReceiveFinalise,
		onCloseAlertModal,
		onCloseModalForcePartReceive,
		closingHistoryModal,
	};
};
